import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, ExternalLink, CheckCircle, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ChangeRequests() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [responseNotes, setResponseNotes] = useState("");

  const { data: requests, isLoading } = useQuery({
    queryKey: ["change-requests"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("estimate_change_requests")
        .select("*, estimates(estimate_number, customer_name)")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const { error } = await supabase
        .from("estimate_change_requests")
        .update({
          status,
          response_notes: notes,
          responded_at: status === "completed" ? new Date().toISOString() : null,
        })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["change-requests"] });
      toast.success("Request updated");
      setSelectedRequest(null);
      setResponseNotes("");
    },
  });

  const handleMarkComplete = (request: any) => {
    setSelectedRequest(request);
  };

  const handleSubmitResponse = () => {
    if (selectedRequest) {
      updateStatus.mutate({
        id: selectedRequest.id,
        status: "completed",
        notes: responseNotes,
      });
    }
  };

  const pendingRequests = requests?.filter(r => r.status === "pending") || [];
  const reviewingRequests = requests?.filter(r => r.status === "reviewing") || [];
  const completedRequests = requests?.filter(r => r.status === "completed") || [];

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      materials: "bg-blue-500",
      pricing: "bg-green-500",
      timeline: "bg-yellow-500",
      scope: "bg-purple-500",
      warranty: "bg-orange-500",
      general: "bg-gray-500",
    };
    return colors[category] || colors.general;
  };

  const RequestCard = ({ request }: { request: any }) => (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <CardTitle className="text-lg">
                {request.estimates?.estimate_number || "N/A"} - {request.customer_name}
              </CardTitle>
              <Badge className={getCategoryBadge(request.category)}>
                {request.category}
              </Badge>
            </div>
            <CardDescription>
              {new Date(request.created_at).toLocaleDateString()} at{" "}
              {new Date(request.created_at).toLocaleTimeString()}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-muted-foreground mb-2">Requested Changes:</p>
            <p className="text-sm bg-muted/30 p-3 rounded-md">{request.requested_changes}</p>
          </div>

          {request.customer_email && (
            <p className="text-sm text-muted-foreground">
              Email: <a href={`mailto:${request.customer_email}`} className="text-primary hover:underline">{request.customer_email}</a>
            </p>
          )}
          {request.customer_phone && (
            <p className="text-sm text-muted-foreground">
              Phone: <a href={`tel:${request.customer_phone}`} className="text-primary hover:underline">{request.customer_phone}</a>
            </p>
          )}

          {request.response_notes && (
            <div className="border-t pt-4">
              <p className="text-sm text-muted-foreground mb-2">Response:</p>
              <p className="text-sm bg-primary/5 p-3 rounded-md">{request.response_notes}</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/admin/estimates/builder/${request.estimate_id}`)}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Estimate
            </Button>

            {request.status === "pending" && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateStatus.mutate({ id: request.id, status: "reviewing" })}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Mark Reviewing
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleMarkComplete(request)}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Mark Complete
                </Button>
              </>
            )}

            {request.status === "reviewing" && (
              <Button
                size="sm"
                onClick={() => handleMarkComplete(request)}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Complete
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <MessageSquare className="h-8 w-8" />
          Change Requests
        </h1>
        <p className="text-muted-foreground mt-2">
          Customer feedback and modification requests for estimates
        </p>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" className="relative">
            Pending
            {pendingRequests.length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {pendingRequests.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reviewing">
            Reviewing
            {reviewingRequests.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {reviewingRequests.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          {pendingRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No pending change requests
              </CardContent>
            </Card>
          ) : (
            pendingRequests.map(request => (
              <RequestCard key={request.id} request={request} />
            ))
          )}
        </TabsContent>

        <TabsContent value="reviewing" className="mt-6">
          {reviewingRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No requests under review
              </CardContent>
            </Card>
          ) : (
            reviewingRequests.map(request => (
              <RequestCard key={request.id} request={request} />
            ))
          )}
        </TabsContent>

        <TabsContent value="completed" className="mt-6">
          {completedRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                No completed requests
              </CardContent>
            </Card>
          ) : (
            completedRequests.map(request => (
              <RequestCard key={request.id} request={request} />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* Response Dialog */}
      {selectedRequest && (
        <Card className="fixed bottom-6 right-6 w-96 shadow-lg z-50">
          <CardHeader>
            <CardTitle>Complete Request</CardTitle>
            <CardDescription>Add notes about how you addressed this request</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={responseNotes}
              onChange={(e) => setResponseNotes(e.target.value)}
              placeholder="What changes did you make? What did you communicate to the customer?"
              rows={4}
            />
            <div className="flex gap-2">
              <Button onClick={handleSubmitResponse}>
                Submit & Mark Complete
              </Button>
              <Button variant="outline" onClick={() => setSelectedRequest(null)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
